import { normalizeNodeGroups } from './nodeGroups';
import type { ProjectDocument } from '../types/project';
import type { ValidationIssue } from '../types/validation';
import { issueFieldPath, issueNodeId } from '../types/validation';

// ------------------------------------------------------------
// Validation issue navigation / fix helpers
// ------------------------------------------------------------

export type ValidationIssueAction =
  | 'go_to_node'
  | 'remove_stale_group_ref'
  | 'show_group'
  | 'none';

export interface ValidationIssueContext {
  action: ValidationIssueAction;
  nodeId?: string;
  groupIndex?: number;
  groupId?: string;
  groupName?: string;
  staleNodeId?: string;
  summary?: string;
}

export function resolveIssueContext(
  issue: ValidationIssue,
  document: ProjectDocument | null,
): ValidationIssueContext {
  if (!document) return { action: 'none' };

  const fieldPath = issueFieldPath(issue);
  const explicitNodeId = issueNodeId(issue);

  if (explicitNodeId && document.nodes.some((node) => node.id === explicitNodeId)) {
    return {
      action: 'go_to_node',
      nodeId: explicitNodeId,
      summary: 'Open this node on the canvas.',
    };
  }

  if (fieldPath) {
    const nodeFromPath = resolveNodeIdFromFieldPath(fieldPath, document);
    if (nodeFromPath) {
      return {
        action: 'go_to_node',
        nodeId: nodeFromPath,
        summary: 'Open this node on the canvas.',
      };
    }

    const groupContext = resolveNodeGroupFieldPath(fieldPath, document);
    if (groupContext) return groupContext;
  }

  if (issue.code === 'MISSING_NODE_REFERENCE') {
    const staleNodeId = extractQuotedId(issue.message, /unknown node '([^']+)'/);
    const groupName = extractQuotedId(issue.message, /Node group '([^']+)'/);
    const groups = normalizeNodeGroups(document);
    const groupIndex = groups.findIndex(
      (entry) => entry.id === groupName || entry.name === groupName,
    );
    const group = groupIndex >= 0 ? groups[groupIndex] : undefined;
    if (staleNodeId) {
      const validIds = group?.node_ids.filter((id) =>
        document.nodes.some((node) => node.id === id),
      );
      return {
        action: 'remove_stale_group_ref',
        staleNodeId,
        groupIndex: groupIndex >= 0 ? groupIndex : undefined,
        groupId: group?.id,
        groupName: group?.name ?? groupName,
        summary:
          'This node was deleted but is still listed in a node group. Remove the stale reference to run simulation.',
        ...(validIds && validIds.length > 0
          ? { nodeId: validIds[0] }
          : {}),
      };
    }
  }

  return { action: 'none' };
}

function resolveNodeIdFromFieldPath(
  fieldPath: string,
  document: ProjectDocument,
): string | undefined {
  const match = /^nodes\[(\d+)\]/.exec(fieldPath);
  if (!match) return undefined;
  const index = Number(match[1]);
  const nodeId = document.nodes[index]?.id;
  if (!nodeId) return undefined;
  return document.nodes.some((node) => node.id === nodeId) ? nodeId : undefined;
}

function resolveNodeGroupFieldPath(
  fieldPath: string,
  document: ProjectDocument,
): ValidationIssueContext | null {
  const staleRefMatch = /^node_groups\[(\d+)\]\.node_ids\[(\d+)\]$/.exec(fieldPath);
  if (staleRefMatch) {
    const groupIndex = Number(staleRefMatch[1]);
    const nodeIndex = Number(staleRefMatch[2]);
    const groups = normalizeNodeGroups(document);
    const group = groups[groupIndex];
    const staleNodeId = group?.node_ids[nodeIndex];
    if (!group || !staleNodeId) return null;

    const validIds = group.node_ids.filter((id) =>
      document.nodes.some((node) => node.id === id),
    );

    return {
      action: 'remove_stale_group_ref',
      groupIndex,
      groupId: group.id,
      groupName: group.name,
      staleNodeId,
      summary:
        'This node was deleted but is still listed in a node group. Remove the stale reference to run simulation.',
      ...(validIds.length > 0 ? { nodeId: validIds[0] } : {}),
    };
  }

  const groupMatch = /^node_groups\[(\d+)\]/.exec(fieldPath);
  if (!groupMatch) return null;

  const groupIndex = Number(groupMatch[1]);
  const group = normalizeNodeGroups(document)[groupIndex];
  if (!group) return null;

  const validIds = group.node_ids.filter((id) =>
    document.nodes.some((node) => node.id === id),
  );

  if (validIds.length === 0) {
    return {
      action: 'remove_stale_group_ref',
      groupIndex,
      groupId: group.id,
      groupName: group.name,
      summary:
        'This node group has no remaining nodes on the canvas. Remove stale references to run simulation.',
    };
  }

  return {
    action: 'show_group',
    groupIndex,
    groupId: group.id,
    groupName: group.name,
    nodeId: validIds[0],
    summary: 'Show the remaining nodes from this group on the canvas.',
  };
}

function extractQuotedId(message: string, pattern: RegExp): string | undefined {
  const match = pattern.exec(message);
  return match?.[1];
}

export function collectStaleNodeGroupRefs(
  issues: ValidationIssue[],
  document: ProjectDocument | null,
): Array<{ groupIndex?: number; staleNodeId: string }> {
  const refs: Array<{ groupIndex?: number; staleNodeId: string }> = [];
  const seen = new Set<string>();

  for (const issue of issues) {
    const context = resolveIssueContext(issue, document);
    if (context.action !== 'remove_stale_group_ref' || !context.staleNodeId) {
      continue;
    }
    const key = `${context.groupIndex ?? 'any'}:${context.staleNodeId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push({
      groupIndex: context.groupIndex,
      staleNodeId: context.staleNodeId,
    });
  }

  return refs;
}
